import React from 'react'
import styled from 'styled-components'
import SidebarNavButton from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/SidebarNav/SidebarNavButton'
import NewSidebarNavButton from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem/SidebarNav/NewSidebarNavButton'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import {DragDropContext, Droppable, DropResult} from 'react-beautiful-dnd'
import {useEditMode} from 'Event/EditModeProvider'
import {createPositions, HashMap, orderedIdsByPosition} from 'lib/list'
import {RemoveButton} from 'organization/Event/DashboardConfig/ConfigDialog'
import VisibleOnMatch from 'Event/attendee-rules/VisibleOnMatch'
import {useEditSidebarItem} from 'Event/template/SimpleBlog/Dashboard/Sidebar/SidebarItem'
import Section from 'Event/template/SimpleBlog/Dashboard/Sidebar/Section'
import Scheduled from 'lib/ui/layout/Scheduled'
import Grid from '@material-ui/core/Grid'
import {SidebarNavProps, SIDEBAR_NAV} from 'Event/template'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import {useRemoveTranslations} from 'Event/LanguageProvider'
import {NavButtonWithSize} from 'Event/Dashboard/components/NavButton'

export const createSidebarNav = (): SidebarNavProps => ({
  type: SIDEBAR_NAV,
  buttons: {},
})

export default function SidebarNav(props: SidebarNavProps & {id: string}) {
  return (
    <Section>
      <Content {...props} />
    </Section>
  )
}

function Content(props: SidebarNavProps & {id: string}) {
  const isEditMode = useEditMode()
  const {buttons} = props

  const hasButtons = Object.keys(buttons).length > 0
  if (!hasButtons) {
    return (
      <EditModeOnly>
        <EmptyConfig {...props} />
      </EditModeOnly>
    )
  }

  const ids = orderedIdsByPosition(buttons)
  const buttonComponents = ids.map((id, index) => {
    const button = buttons[id]
    return (
      <VisibleOnMatch rules={button.rules} key={id}>
        <Scheduled component={button}>
          <StyledGrid item xs={12} md={button.size}>
            <SidebarNavButton
              navId={props.id}
              {...button}
              nav={props}
              id={id}
              backgroundColor={button.backgroundColor}
              hoverBackgroundColor={button.hoverBackgroundColor}
              textColor={button.textColor}
              borderWidth={button.borderWidth}
              borderRadius={button.borderRadius}
              borderColor={button.borderColor}
              index={index}
            />
          </StyledGrid>
        </Scheduled>
      </VisibleOnMatch>
    )
  })

  if (!isEditMode) {
    return <>{buttonComponents}</>
  }

  return <DraggableList {...props}>{buttonComponents}</DraggableList>
}

function EmptyConfig(props: SidebarNavProps & {id: string}) {
  return (
    <>
      <RemoveSidebarNavButton id={props.id} buttons={props.buttons} />
      <StyledNewSidebarNavButton nav={props} navId={props.id} />
    </>
  )
}
function DraggableList(
  props: SidebarNavProps & {children: React.ReactElement[]; id: string},
) {
  const handleDrag = useHandleDrag(props)

  return (
    <>
      <RemoveSidebarNavButton id={props.id} buttons={props.buttons} />
      <DragDropContext onDragEnd={handleDrag}>
        <Droppable droppableId="sidebar_nav_buttons">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {props.children}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <StyledNewSidebarNavButton nav={props} navId={props.id} />
      </DragDropContext>
    </>
  )
}

function RemoveSidebarNavButton(props: {
  id: string
  buttons: HashMap<NavButtonWithSize>
}) {
  const {id, buttons} = props

  const saveTemplate = useSaveTemplate()
  const removeTranslations = useRemoveTranslations()

  const buttonKeys = Object.keys(buttons).flatMap((id) => [`${id}_text`])

  const remove = () => {
    saveTemplate({
      sidebarItems: {
        [id]: REMOVE,
      },
      localization: removeTranslations(buttonKeys),
    })
  }

  return (
    <RemoveButton onClick={remove} size="large">
      Remove Buttons
    </RemoveButton>
  )
}

function useHandleDrag(props: SidebarNavProps) {
  const {update} = useEditSidebarItem()

  return (result: DropResult) => {
    const {destination, source} = result

    if (!destination) {
      return
    }

    const ids = orderedIdsByPosition(props.buttons)
    const [removed] = ids.splice(source.index, 1)
    ids.splice(destination.index, 0, removed)

    update({
      buttons: createPositions(ids),
    })
  }
}

const StyledNewSidebarNavButton = styled(NewSidebarNavButton)`
  margin-top: ${(props) => props.theme.spacing[6]} !important;
  margin-bottom: ${(props) => props.theme.spacing[5]}!important;
`

const StyledGrid = styled(Grid)`
  margin: 0 auto;
`
